import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { searchTenants } from '../../api/admin/endpoints';
import type { TenantSortKey } from '../../api/admin/types';
import type { LicenceState } from '../../api/shared';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDate } from '../../components/format';
import { LicenceStateChip } from '../../components/LicenceStateChip';
import { PageHeader } from '../../components/PageHeader';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { CreateTenantDialog } from './CreateTenantDialog';

const LICENCE_STATES: Array<LicenceState> = [
  'ACTIVE',
  'AWAITING_PAYMENT',
  'SUSPENDED',
  'EXPIRED',
  'NOT_YET_VALID',
];

/** Every business on the platform, server-paginated and server-sorted (Conventions/08 §3). */
export const TenantsPage = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [licenceState, setLicenceState] = useState<LicenceState | ''>('');
  const [page, setPage] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const sort = useSort<TenantSortKey>('createdAt', 'desc', () => setPage(0));
  const debouncedTerm = useDebouncedValue(term);

  const query = useQuery({
    queryKey: ['tenants', debouncedTerm, licenceState, page, sort.key, sort.direction],
    queryFn: () =>
      searchTenants({
        term: debouncedTerm,
        licenceState: licenceState || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('tenants.title')}
        subtitle={t('tenants.subtitle')}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreating(true)}>
            {t('tenants.create')}
          </Button>
        }
      />
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('tenants.searchHint')}
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              setPage(0);
            }}
          />
          <TextField
            select
            size="small"
            label={t('field.licenceState')}
            value={licenceState}
            onChange={(event) => {
              setLicenceState(event.target.value as LicenceState | '');
              setPage(0);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {LICENCE_STATES.map((state) => (
              <MenuItem key={state} value={state}>
                {t(`licenceState.${state}` as const)}
              </MenuItem>
            ))}
          </TextField>
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="name">
                  {t('field.tenantName')}
                </SortableCell>
                <TableCell>{t('tenants.owner')}</TableCell>
                <TableCell>{t('field.licenceState')}</TableCell>
                <SortableCell sort={sort} column="validUntil">
                  {t('licence.validUntil')}
                </SortableCell>
                <SortableCell sort={sort} column="createdAt">
                  {t('field.createdAt')}
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={5} />
              ) : (
                rows.map((tenant) => (
                  <TableRow
                    key={tenant.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                  >
                    <TableCell sx={{ fontWeight: 560 }}>{tenant.name}</TableCell>
                    <TableCell>{tenant.ownerEmail}</TableCell>
                    <TableCell>
                      <LicenceStateChip state={tenant.licenceState} />
                    </TableCell>
                    <TableCell>
                      {tenant.validUntil ? formatDate(tenant.validUntil, language) : t('licence.noEnd')}
                    </TableCell>
                    <TableCell>{formatDate(tenant.createdAt, language)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState
            title={t('tenants.empty')}
            detail={t('tenants.emptyDetail')}
            action={
              <Button onClick={() => setIsCreating(true)} startIcon={<AddIcon />}>
                {t('tenants.create')}
              </Button>
            }
          />
        ) : null}
        <ServerPagination
          page={page}
          pageSize={query.data?.pageSize ?? 25}
          total={query.data?.total ?? 0}
          onPageChange={setPage}
        />
      </Paper>
      {isCreating ? (
        <CreateTenantDialog
          onClose={() => setIsCreating(false)}
          onCreated={(tenant) => {
            setIsCreating(false);
            navigate(`/platform/tenants/${tenant.id}`);
          }}
        />
      ) : null}
    </>
  );
};
